import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PartialStar, starState } from "@/components/ReaderReviews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#69d2c7";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ReviewForm({ onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!rating) e.rating = "Please select a star rating.";
    const t = text.trim();
    if (!t) e.text = "Please write your review.";
    else if (t.length < 50) e.text = "Please write at least 50 characters.";
    else if (t.length > 1500) e.text = "Please keep your review under 1,500 characters.";
    const em = email.trim();
    if (!em) e.email = "Email is required.";
    else if (!EMAIL_RE.test(em)) e.email = "Please enter a valid email address.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        rating,
        text: text.trim(),
        email: email.trim(),
        name: name.trim() || null,
        website,
      });
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      if (err.response?.status === 429) {
        setServerError(
          typeof err.response.data?.detail === "string"
            ? err.response.data.detail
            : "You've already submitted three reviews. Try again later."
        );
      } else {
        setServerError("Something went wrong. Please try again.");
      }
      setLoading(false);
    }
  };

  const displayRating = hoverRating || rating;

  const pickRating = (v) => {
    setRating(v);
    setErrors((p) => ({ ...p, rating: undefined }));
  };

  const handleStarKey = (e) => {
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") next = Math.min(5, (rating || 0.5) + 0.5);
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = Math.max(1, (rating || 1.5) - 0.5);
    else if (e.key === "Home") next = 1;
    else if (e.key === "End") next = 5;
    if (next !== null) {
      e.preventDefault();
      pickRating(next);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto border border-neutral-200 bg-white p-6 sm:p-8 text-left" data-testid="review-form-card">
      {submitted ? (
        <div className="text-center py-8" data-testid="review-success">
          <p className="text-xl font-bold">Thanks.</p>
          <p className="mt-2 text-base text-neutral-600">Your review should appear shortly.</p>
          <Link
            to="/free-book"
            className="mt-6 inline-block text-sm font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
            style={{ textDecorationColor: TEAL }}
            data-testid="review-back-link"
          >
            Back to the book →
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate data-testid="review-form">
          <div>
            <span className="block text-sm font-semibold mb-2" id="review-rating-label">Rating</span>
            <div className="flex items-center">
              <div
                role="slider"
                tabIndex={0}
                aria-labelledby="review-rating-label"
                aria-valuemin={1}
                aria-valuemax={5}
                aria-valuenow={rating || 0}
                aria-valuetext={rating ? `${rating} out of 5` : "No rating selected"}
                onKeyDown={handleStarKey}
                onMouseLeave={() => setHoverRating(0)}
                className="inline-flex gap-1 outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                data-testid="review-star-slider"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className="relative inline-block p-0.5">
                    <PartialStar state={starState(displayRating, n)} size={30} />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-1/2"
                      onClick={() => pickRating(n - 0.5)}
                      onMouseEnter={() => setHoverRating(n - 0.5)}
                      data-testid={`review-star-${n}-half`}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-hidden="true"
                      className="absolute inset-y-0 right-0 w-1/2"
                      onClick={() => pickRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      data-testid={`review-star-${n}`}
                    />
                  </span>
                ))}
              </div>
            </div>
            <p className="mt-1 text-xs text-neutral-400">Tap the left half of a star for a half rating.</p>
            {errors.rating && (
              <p className="mt-2 text-sm text-red-600" role="alert" data-testid="review-rating-error">
                {errors.rating}
              </p>
            )}
          </div>

          <div className="mt-6">
            <label htmlFor="review-text" className="block text-sm font-semibold mb-1.5">
              Your review
            </label>
            <textarea
              id="review-text"
              required
              rows={6}
              maxLength={1500}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setErrors((p) => ({ ...p, text: undefined }));
              }}
              placeholder="What worked, what did not, what still feels unfinished."
              data-testid="review-text-input"
              className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400 resize-y"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-neutral-400">Minimum 50 characters.</span>
              <span
                className={text.length > 0 && text.length < 50 ? "text-red-600 font-semibold" : "text-neutral-400"}
                data-testid="review-char-counter"
              >
                {text.length} / 1500
              </span>
            </div>
            {errors.text && (
              <p className="mt-1 text-sm text-red-600" role="alert" data-testid="review-text-error">
                {errors.text}
              </p>
            )}
          </div>

          <div className="mt-5">
            <label htmlFor="review-email" className="block text-sm font-semibold mb-1.5">
              Email
            </label>
            <input
              id="review-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((p) => ({ ...p, email: undefined }));
              }}
              placeholder="you@example.com"
              data-testid="review-email-input"
              className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
            />
            <p className="mt-1.5 text-xs text-neutral-400" data-testid="review-email-privacy-note">
              Your email will not be shown in the public review.
            </p>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600" role="alert" data-testid="review-email-error">
                {errors.email}
              </p>
            )}
          </div>

          <div className="mt-5">
            <label htmlFor="review-name" className="block text-sm font-semibold mb-1.5">
              Name <span className="font-normal text-neutral-400">(optional)</span>
            </label>
            <input
              id="review-name"
              type="text"
              maxLength={100}
              autoComplete="nickname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nickname or first name"
              data-testid="review-name-input"
              className="w-full border border-neutral-300 focus:border-neutral-900 outline-none text-base px-4 py-3 placeholder:text-neutral-400"
            />
            <p className="mt-1.5 text-xs text-neutral-400">
              Left blank, your review appears as “Anonymous trader”.
            </p>
          </div>

          <div style={{ position: "absolute", left: "-9999px", top: "auto" }} aria-hidden="true">
            <label htmlFor="review-website">Website</label>
            <input
              id="review-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              data-testid="review-honeypot-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="review-submit-button"
            className="mt-7 w-full text-lg font-extrabold py-4 px-8 bg-neutral-950 text-white hover:bg-neutral-800 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Submit review"}
          </button>
          {serverError && (
            <p className="mt-3 text-sm text-red-600" role="alert" data-testid="review-server-error">
              {serverError}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
